import { useState, useCallback, useMemo } from 'react';
import { loadMultipleExpenseFiles } from '../utils/firestoreStorage';

/**
 * Manages multi-file selection and merging for export.
 *
 * @param {string|null} uid           Firebase uid (null = guest, disables loading)
 * @param {object[]}    baseExpenses  Expenses from the currently active file
 * @param {object[]}    savedFiles    Full list of saved files from useFileOperations
 * @param {string|null} currentFileName  Name of the active file (excluded from selection)
 */
export function useMergedExpenses(uid, baseExpenses, savedFiles, currentFileName) {
  // Set of filenames the user has checked
  const [selectedNames, setSelectedNames] = useState(new Set());
  // Cache: filename → expense[]
  const [cache, setCache] = useState({});
  // Load state per file: 'loading' | 'loaded' | 'error'
  const [loadState, setLoadState] = useState({});

  const otherFiles = useMemo(
    () => savedFiles.filter(f => f.name !== currentFileName && f.reportType !== 'credit_card' && f.reportType !== 'bank'),
    [savedFiles, currentFileName]
  );

  const toggleFile = useCallback(async (name) => {
    setSelectedNames(prev => {
      const next = new Set(prev);
      if (next.has(name)) {
        next.delete(name);
      } else {
        next.add(name);
      }
      return next;
    });

    // If already cached or loading, nothing more to do
    if (cache[name] || loadState[name] === 'loading') return;

    // Check if inline data is available from savedFiles (avoids a Firestore round-trip)
    const inFile = savedFiles.find(f => f.name === name);
    if (inFile?.data) {
      setCache(prev => ({ ...prev, [name]: inFile.data }));
      setLoadState(prev => ({ ...prev, [name]: 'loaded' }));
      return;
    }

    // Fall back to Firestore fetch
    if (!uid) return;
    setLoadState(prev => ({ ...prev, [name]: 'loading' }));
    try {
      const results = await loadMultipleExpenseFiles(uid, [name]);
      const data = results[0]?.data ?? [];
      setCache(prev => ({ ...prev, [name]: data }));
      setLoadState(prev => ({ ...prev, [name]: 'loaded' }));
    } catch {
      setLoadState(prev => ({ ...prev, [name]: 'error' }));
    }
  }, [uid, savedFiles, cache, loadState]);

  const mergedExpenses = useMemo(() => {
    const seen = new Set();
    const result = [];

    const addAll = (list, sourceName) => {
      for (const e of list) {
        const key = String(e.id);
        if (!seen.has(key)) {
          seen.add(key);
          // Tag with source file name so exports can attribute each row
          result.push(sourceName ? { ...e, _sourceFile: sourceName } : e);
        }
      }
    };

    addAll(baseExpenses, currentFileName || null);
    for (const name of selectedNames) {
      if (cache[name]) addAll(cache[name], name);
    }
    return result;
  }, [baseExpenses, selectedNames, cache, currentFileName]);

  const isLoadingAny = [...selectedNames].some(n => loadState[n] === 'loading');

  const reset = useCallback(() => {
    setSelectedNames(new Set());
    // Keep cache — it's still valid data
  }, []);

  return {
    otherFiles,
    selectedNames,
    toggleFile,
    loadState,
    mergedExpenses,
    isLoadingAny,
    reset,
  };
}
